// Module 12 — adaptive-performance governor v2 (3-state machine + PROACTIVE PROBING).
//
// GOAL (user requirement): maximize QUALITY — find the highest fidelity tier the device can hold above
// the 28 fps hard floor, and CLIMB there proactively. 28 fps is acceptable, ~35 is comfortable; the old
// v1 governor trapped mid-range devices at a low tier because its "good" threshold was 55 fps and it only
// REACTED downward, never probed up. v2 fixes both:
//
//   • Hard floor 28 fps (was 30). Quality is only sacrificed below this.
//   • Probe target 40 fps. Whenever the device holds ≥40 fps it tries one tier UP every few seconds; if
//     the probe keeps fps ≥28 it stays, otherwise it rolls back immediately (1 bad sample) and cools down.
//   • Six tiers (ultra/highplus/high/mediumplus/medium/low) → smaller steps, less visible pop.
//
// The machine has THREE states:
//   STABLE    — fps in [28, 40). Hold; tick the probe countdown.
//   DEGRADED  — sustained <28 fps. Step ONE tier DOWN (fidelity first, pace second). Hysteresis-protected.
//   PROBING   — just stepped UP to sample headroom; watch the next window. Confirm (stay) or rollback.
//
// TWO ORDERED knobs (unchanged from v1, R15 determinism): fidelity FIRST (viz.setQuality), pace SECOND
// (state.setSpeed) only when fidelity is floored AND apply-lag persists. Restore reverses the order.
// The governor touches ONLY pacing/fidelity — never the command stream, selection, or snapshot sequence,
// so determinism holds on every device. Pure state machine + an injectable rAF + clock; no module internals.
import type { QualityTier } from "../viz/index";

// best→worst. The two INTERMEDIATE tiers (v1.1.1) let the governor step fidelity in smaller increments,
// so probing up/down produces less visible pop than the old 4-tier ladder.
const QUALITY: readonly QualityTier[] = ["ultra", "highplus", "high", "mediumplus", "medium", "low"];

/** The three operating modes — surfaced to the perf HUD so the user can SEE the trade being made. */
export type GovernorMode = "stable" | "degraded" | "probing";

export interface GovernorDeps {
  setQuality: (tier: QualityTier) => void; // viz.setQuality — fidelity knob
  setSpeed: (speed: number) => void; // state.setSpeed — pace knob (worker tick-Hz)
  startQuality?: QualityTier; // hardware/env-detected starting tier (default "high"); governor floats from here
  speedLadder?: number[]; // descending allowed speeds (index 0 = fastest), default [4,2,1]
  // --- v2 thresholds (quality-first tuning) ---
  hardFloorFps?: number; // BELOW this → DEGRADE (default 28 — "30 acceptable, 28 the floor" per user)
  probeTargetFps?: number; // AT/ABOVE this → eligible to PROBE up (default 40)
  degradeStreak?: number; // consecutive floor-violating samples before one degrade step (default 3)
  probeIntervalMs?: number; // in STABLE, wait this long between probe attempts (default 4000)
  probeWindowSamples?: number; // samples to confirm a probe before locking it in (default 6)
  probeCooldownMs?: number; // after a failed probe, wait this long before probing again (default 6000)
  lagBudgetMs?: number; // apply-lag above which pace may drop once fidelity is floored (default 50)
  emaAlpha?: number; // EMA smoothing for the HUD fps readout (default 0.2)
  // injectable loop seams (default to the DOM globals); kept here so the loop is node-testable.
  requestFrame?: (cb: () => void) => number;
  cancelFrame?: (id: number) => void;
  now?: () => number;
  getApplyLagMs?: () => number;
}

export interface Governor {
  /** Feed one rAF frame's measured main-thread time + the latest snapshot-apply lag. */
  sample(frameMs: number, applyLagMs: number): void;
  /** Force a re-probe NOW (e.g. on a battery/charging state change). Safe no-op if already probing. */
  requestReprobe(): void;
  readonly quality: QualityTier;
  readonly speed: number;
  readonly fps: number; // smoothed (EMA) main-thread frame rate; 0 before any sample
  readonly mode: GovernorMode; // STABLE / DEGRADED / PROBING — for the perf HUD
  start(): void;
  stop(): void;
}

export function makeGovernor(deps: GovernorDeps): Governor {
  const ladder = deps.speedLadder ?? [4, 2, 1];
  const hardFloorFps = deps.hardFloorFps ?? 28;
  const probeTargetFps = deps.probeTargetFps ?? 40;
  const degradeStreak = deps.degradeStreak ?? 3;
  const probeIntervalMs = deps.probeIntervalMs ?? 4000;
  const probeWindowSamples = deps.probeWindowSamples ?? 6;
  const probeCooldownMs = deps.probeCooldownMs ?? 6000;
  const lagBudgetMs = deps.lagBudgetMs ?? 50;
  const alpha = deps.emaAlpha ?? 0.2;
  const floorMs = 1000 / hardFloorFps; // a sample is "bad" if frame time exceeds this
  const probeMs = 1000 / probeTargetFps; // a sample is "headroom" if frame time is at or below this

  // start at the hardware/env-detected tier (default "high"); index into QUALITY = best→worst.
  const startIdx = Math.max(0, QUALITY.indexOf(deps.startQuality ?? "high"));
  let qIdx = startIdx;
  let spIdx = 0; // 0=fastest … ladder.length-1=slowest
  let emaMs = 0; // exponential moving average of frame time → the HUD's FPS readout

  // --- state machine ---
  let mode: GovernorMode = "stable";
  let badStreak = 0; // consecutive floor-violating samples
  // lastChangeAt is lazily set on the FIRST sample (the effective "boot" wall-time); this means the probe
  // cadence is measured from the first observed frame, not from construction (when `now` isn't injected
  // yet). lastProbeAttemptAt starts at -∞ so cooldown is NOT blocking at boot — cooldown binds only AFTER
  // a probe rolls back (rollbackProbe overwrites it to the rollback time).
  let lastChangeAt = Number.NaN; // sentinel: "not yet booted" → set on first sample
  let probeConfirmCount = 0; // good samples accumulated in PROBING before locking in
  let lastProbeAttemptAt = Number.NEGATIVE_INFINITY;

  function setTier(idx: number): void {
    qIdx = idx;
    deps.setQuality(QUALITY[qIdx]!);
    lastChangeAt = nowMs();
  }
  function setPace(idx: number): void {
    spIdx = idx;
    deps.setSpeed(ladder[spIdx]!);
    lastChangeAt = nowMs();
  }

  /** Fidelity-first, pace-second — R15 determinism (unchanged ordering from v1). */
  function degrade(applyLagMs: number): void {
    if (qIdx < QUALITY.length - 1) {
      setTier(qIdx + 1); // fidelity first
    } else if (applyLagMs > lagBudgetMs && spIdx < ladder.length - 1) {
      setPace(spIdx + 1); // pace only when fidelity floored AND apply still lags
    }
  }

  /** Pace-first, fidelity-second — reverse of degrade. */
  function restore(): void {
    if (spIdx > 0) {
      setPace(spIdx - 1);
    } else if (qIdx > 0) {
      setTier(qIdx - 1);
    }
  }

  /** Try one tier UP. Returns true if a probe actually started (false at ceiling). */
  function tryProbeUp(): boolean {
    if (qIdx <= 0) return false; // already at ultra
    setTier(qIdx - 1);
    mode = "probing";
    probeConfirmCount = 0;
    lastProbeAttemptAt = nowMs();
    return true;
  }

  /** Probe failed — roll back one tier and enter cooldown before the next attempt. */
  function rollbackProbe(): void {
    setTier(Math.min(qIdx + 1, QUALITY.length - 1));
    mode = "stable";
    lastProbeAttemptAt = nowMs(); // start cooldown now
    badStreak = 0;
    probeConfirmCount = 0;
  }

  /** Confirm a probe — lock it in, return to STABLE so the next probe can fire after the interval. */
  function confirmProbe(): void {
    mode = "stable";
    lastChangeAt = nowMs(); // restart the STABLE timer so we don't immediately re-probe
    probeConfirmCount = 0;
  }

  let nowMs: () => number = deps.now ?? (() => performance.now());

  const gov: Governor = {
    sample(frameMs, applyLagMs): void {
      // smooth frame time for the HUD; ignore non-positive deltas from a paused/background tab.
      if (frameMs > 0) emaMs = emaMs === 0 ? frameMs : emaMs * (1 - alpha) + frameMs * alpha;
      const t = nowMs();
      // First-ever sample: arm the cadence timer from this wall-time so the first probe waits one full
      // probeIntervalMs (avoids a probe on the very first headroom frame after boot).
      if (Number.isNaN(lastChangeAt)) lastChangeAt = t;
      const isBad = frameMs > floorMs; // below the hard floor
      const isHeadroom = frameMs <= probeMs; // at/above the probe target (room to climb)

      if (mode === "probing") {
        // PROBING: a SINGLE floor violation rolls back immediately (no streak needed — probes must be
        // cheap to abandon). Otherwise accumulate confirmations; once enough, lock the probe in.
        if (isBad) {
          rollbackProbe();
        } else {
          probeConfirmCount++;
          if (probeConfirmCount >= probeWindowSamples) confirmProbe();
        }
        return;
      }

      // STABLE / DEGRADED: count floor-violation streaks for degrade (hysteresis). A headroom streak
      // in STABLE arms a probe once the probe interval (or cooldown) has elapsed.
      if (isBad) {
        badStreak++;
        if (badStreak >= degradeStreak) {
          badStreak = 0;
          degrade(applyLagMs);
          mode = "degraded";
        }
        return;
      }
      // not bad — clear the streak (neutral/headroom both reset the degrade counter)
      badStreak = 0;

      // RESTORE: when headroom returns AFTER a degrade, climb back one step (pace first, then fidelity)
      // after a short hold, then settle toward STABLE. This is the v1 hysteresis restore, preserved.
      if (mode === "degraded" && isHeadroom) {
        // gate restore on a hold since the last change to avoid ping-pong with degrade (hysteresis)
        if (t - lastChangeAt >= probeIntervalMs / 2) {
          restore();
          mode = (qIdx === startIdx && spIdx === 0) ? "stable" : "degraded";
        }
        return;
      }

      // STABLE + headroom → PROBE upward on the cadence (every probeIntervalMs), unless in cooldown.
      if (mode === "stable" && isHeadroom && qIdx > 0) {
        const sinceProbe = t - lastProbeAttemptAt;
        const sinceChange = t - lastChangeAt;
        const cadenceOk = sinceChange >= probeIntervalMs;
        const cooldownOk = sinceProbe >= probeCooldownMs;
        if (cadenceOk && cooldownOk) {
          tryProbeUp();
        }
      }
    },

    requestReprobe(): void {
      // An ambient signal changed (e.g. plugged into mains, or battery recovered): try to climb now by
      // bypassing the cadence + cooldown timers. No-op if already mid-probe (let it resolve first).
      if (mode !== "probing") {
        const t = nowMs();
        lastProbeAttemptAt = Math.min(lastProbeAttemptAt, t - probeCooldownMs); // bypass cooldown
        lastChangeAt = Math.min(lastChangeAt, t - probeIntervalMs); // bypass cadence
      }
    },

    get quality() { return QUALITY[qIdx]!; },
    get speed() { return ladder[spIdx]!; },
    get fps() { return emaMs > 0 ? Math.round(1000 / emaMs) : 0; },
    get mode() { return mode; },

    start(): void {
      if (running) return;
      running = true;
      const raf = deps.requestFrame ?? ((cb) => requestAnimationFrame(cb));
      const getLag = deps.getApplyLagMs ?? (() => 0);
      const t0 = nowMs();
      // lastChangeAt is armed lazily on the first sample (above); lastProbeAttemptAt stays -∞ so there is
      // no cooldown at boot — cooldown binds only after a probe rolls back.
      lastProbeAttemptAt = Number.NEGATIVE_INFINITY;
      let last = t0;
      const loop = (): void => {
        if (!running) return;
        const t = nowMs();
        gov.sample(t - last, getLag());
        last = t;
        rafId = raf(loop);
      };
      rafId = raf(loop);
    },

    stop(): void {
      running = false;
      (deps.cancelFrame ?? ((id: number) => cancelAnimationFrame(id)))(rafId);
    },
  };

  let running = false;
  let rafId = 0;

  return gov;
}
